import React from 'react';
import type { ClientSummary } from '@/features/Clients/Data';
import type { CollaboratorPublic } from '@/features/Collaborators/Data';
import { calcularAlertaMovimentacao, CLASSE_COR_POR_NIVEL_ALERTA, TIPOS_SOLICITACAO_SEM_MOVIMENTACAO } from '../../../models';
import { TIPO_SOLICITACAO_LABELS } from '../../NovaSolicitacaoPage/novaSolicitacaoPage.definitions';
import { STATUS_MOVIMENTACAO_LABELS } from '../../SolicitacaoDetailsPage/solicitacaoDetailsPage.definitions';
import { pendenciaMovimentacao, pendenciaSolicitacao } from '../../SolicitacaoDetailsPage/pendenciaMovimentacao';
import { STATUS_SOLICITACAO_LABELS, STATUS_SOLICITACAO_BADGE_CLASS } from '../solicitacoesListPage.definitions';
import { movimentacaoRelevante, type LinhaControle, type VisaoControle } from '../solicitacoesLista.utils';
import { dataPrevistaComFluxo } from './solicitacoesKanban.utils';

interface LinhaControleRowProps {
  linha: LinhaControle;
  visao: VisaoControle;
  clientes: ClientSummary[];
  colaboradores: CollaboratorPublic[];
  onSelect: (linha: LinhaControle) => void;
  /** Só na aba "Controle de Solicitações" — é lá que o SI executa essas pendências; nas
   * outras abas (visão de solicitante) o badge só confundiria. */
  mostrarPendencia: boolean;
}

/** Célula comum a alerta/pendência/status — sem borda lateral (as bordas laterais só
 * entram na 1ª e na última célula), pra parecer uma única linha contínua mesmo sendo
 * várias células de grid lado a lado. */
const CELULA_CLASSE = 'flex items-center justify-center border-y border-gray-200 bg-white px-2 py-3 group-hover:bg-gray-50';

/**
 * Uma linha do modo "Lista". Visão Movimentações: alerta, próximo passo e etapa são DESTA
 * Movimentação (e o projeto, o dela). Visão Solicitações: alerta e próximo passo da
 * Solicitação inteira (Movimentação em andamento); a data é a da original.
 *
 * Renderiza como células de um CSS Grid compartilhado com as demais linhas, não como um
 * `<button>` de largura própria — a largura de cada coluna (alerta/pendência/status) fica
 * igual de linha pra linha, como colunas de planilha. `contents` faz este wrapper não gerar
 * caixa própria; `group` ainda destaca as células como uma linha só ao passar o mouse.
 */
export default function LinhaControleRow({ linha, visao, clientes, colaboradores, onSelect, mostrarPendencia }: LinhaControleRowProps) {
  const { solicitacao, movimentacao, movimentacoes } = linha;
  const porMovimentacao = visao === 'movimentacoes' && movimentacao != null;
  const movimentacaoDoAlerta = porMovimentacao ? movimentacao : movimentacaoRelevante(movimentacoes);
  const alerta = movimentacaoDoAlerta ? calcularAlertaMovimentacao(movimentacaoDoAlerta) : null;
  const clienteId = (porMovimentacao ? movimentacao.clienteId : null) ?? solicitacao.clienteId;
  const clienteNome = clientes.find((cliente) => cliente.id === clienteId)?.name ?? null;
  const solicitanteNome =
    colaboradores.find((colaborador) => colaborador.id === solicitacao.solicitanteId)?.name ?? solicitacao.solicitanteEmail;
  // Mostra independente do status — decisão de produto: "o que falta" é útil de ver mesmo
  // antes de começar a tratar (Novo) ou depois (Cancelada/Excluída), não só em andamento.
  const pendencia = !mostrarPendencia
    ? null
    : porMovimentacao
      ? pendenciaMovimentacao(movimentacao)
      : pendenciaSolicitacao(solicitacao, movimentacoes);

  const alertaBadge = alerta && (
    <span className={`rounded-full px-2 py-0.5 text-center text-xs font-semibold ${CLASSE_COR_POR_NIVEL_ALERTA[alerta.nivel]}`}>
      {alerta.emoji} {alerta.texto}
    </span>
  );
  const pendenciaBadge = pendencia && (
    <span className="rounded-full bg-amber-100 px-2 py-0.5 text-center text-xs font-semibold text-amber-800" title="Próximo passo pra concluir">
      {pendencia.emoji} {pendencia.label}
    </span>
  );
  // Coluna do meio "encosta" na de status quando só alerta OU só pendência existe.
  const colunaAlerta = mostrarPendencia && !pendenciaBadge ? null : alertaBadge;
  const colunaPendencia = mostrarPendencia && !pendenciaBadge ? alertaBadge : pendenciaBadge;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onSelect(linha);
    }
  };

  return (
    <div role="button" tabIndex={0} onClick={() => onSelect(linha)} onKeyDown={handleKeyDown} className="group contents cursor-pointer">
      <div className="rounded-l-md border-y border-l border-gray-200 bg-white px-4 py-3 text-left group-hover:bg-gray-50">
        <p className="font-montserrat font-medium text-gray-900">
          {TIPO_SOLICITACAO_LABELS[solicitacao.tipoSolicitacao]}
          {clienteNome && <span className="font-normal text-gray-500"> - {clienteNome}</span>}
        </p>
        <p className="text-sm text-gray-500">{solicitacao.colaboradorNome}</p>
        {!TIPOS_SOLICITACAO_SEM_MOVIMENTACAO.includes(solicitacao.tipoSolicitacao) && (
          <p className="text-xs text-gray-400">
            {dataPrevistaComFluxo(movimentacao) ?? 'Data a definir'}
            {porMovimentacao && ` · ${STATUS_MOVIMENTACAO_LABELS[movimentacao.statusMovimentacao]}`}
          </p>
        )}
        <p className="text-xs text-gray-400">Aberta por: {solicitanteNome}</p>
      </div>
      <div className={CELULA_CLASSE}>{colunaAlerta}</div>
      {mostrarPendencia && <div className={CELULA_CLASSE}>{colunaPendencia}</div>}
      <div className={`${CELULA_CLASSE} rounded-r-md border-r`}>
        <span className={`rounded-full px-2 py-0.5 text-center text-xs font-semibold ${STATUS_SOLICITACAO_BADGE_CLASS[solicitacao.status]}`}>
          {STATUS_SOLICITACAO_LABELS[solicitacao.status]}
        </span>
      </div>
    </div>
  );
}
